import { formatCurrency as formatMoney } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Save, CheckCircle, AlertTriangle, Info, Lock, RefreshCw, FileText, XCircle, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { ShiftReceipts } from '../components/ShiftReceipts';

// Helper to evaluate math expressions in input fields
const evaluateExpression = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  // Check if it contains operators
  if (/[+\-*/]/.test(value)) {
    try {
      // Only allow numbers, operators, spaces, and decimal points
      const sanitized = value.replace(/[^0-9+\-*/.() ]/g, '');
      // eslint-disable-next-line no-eval
      const result = Function('"use strict"; return (' + sanitized + ')')();
      if (typeof result === 'number' && !isNaN(result)) {
        // Return with 2 decimal places, but trim trailing zeros
        return parseFloat(result.toFixed(2));
      }
    } catch (e) {
      // If evaluation fails, return original value
    }
  }
  return value;
};

const CashupPage = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user } = useAuth();
  
  // Get return params for navigation back to shifts page
  const returnBusiness = searchParams.get('returnBusiness') || 'all';
  const returnView = searchParams.get('returnView') || 'pending';
  
  const navigateBack = () => {
    const params = new URLSearchParams();
    if (returnBusiness !== 'all') params.set('business', returnBusiness);
    if (returnView !== 'pending') params.set('view', returnView);
    navigate(`/${params.toString() ? '?' + params.toString() : ''}`);
  };
  
  const [shift, setShift] = useState(null);
  const [cashup, setCashup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [retryingAccounting, setRetryingAccounting] = useState(false);
  
  // Payout matching state
  const [unmatchedPayouts, setUnmatchedPayouts] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  const [matchingPayouts, setMatchingPayouts] = useState(false);
  const [linkedReceipts, setLinkedReceipts] = useState([]);

  const [formData, setFormData] = useState({
    staff_member: '',
    banked_cash: '',
    confirmed_cash_payouts: '',
    reported_cash_tips: '',
    batch_report_total: '',
    reported_card_tips: '',
    recognised_cash_refunds: '',
    recognised_card_refunds: '',
    tip_adjustment_amount: '',
    tip_adjustment_type: 'cash',
    extra_comments: ''
  });

  const [calculations, setCalculations] = useState({
    cash_over_under: 0,
    card_over_under: 0,
    recognised_cash_tips: 0,
    recognised_card_tips: 0,
    total_recognised_tips: 0,
    unrecognised_cash_refunds: 0,
    unrecognised_card_refunds: 0
  });

  useEffect(() => {
    fetchData();
  }, [shiftId]);

  const fetchReceipts = async (businessId) => {
    try {
      const [payoutsRes, linkedRes] = await Promise.all([
        api.get(`/cash-expenses/unmatched-payouts?business_id=${businessId}`),
        api.get(`/cash-expenses/by-shift/${shiftId}`)
      ]);
      setUnmatchedPayouts(payoutsRes.data || []);
      setLinkedReceipts(linkedRes.data || []);
    } catch (e) {
      setUnmatchedPayouts([]);
    }
  };

  const fetchData = async () => {
    try {
      const shiftRes = await api.get(`/shifts/${shiftId}`);
      setShift(shiftRes.data);
      
      await fetchReceipts(shiftRes.data.business_id);
      
      try {
        const cashupRes = await api.get(`/cashups/shift/${shiftId}`);
        if (cashupRes.data) {
          setCashup(cashupRes.data);
          setFormData({
            staff_member: cashupRes.data.staff_member || '',
            banked_cash: cashupRes.data.banked_cash?.toString() || '',
            confirmed_cash_payouts: cashupRes.data.confirmed_cash_payouts?.toString() || '',
            reported_cash_tips: cashupRes.data.reported_cash_tips?.toString() || '',
            batch_report_total: cashupRes.data.batch_report_total?.toString() || '',
            reported_card_tips: cashupRes.data.reported_card_tips?.toString() || '',
            recognised_cash_refunds: cashupRes.data.recognised_cash_refunds?.toString() || '',
            recognised_card_refunds: cashupRes.data.recognised_card_refunds?.toString() || '',
            tip_adjustment_amount: cashupRes.data.tip_adjustment_amount?.toString() || '',
            tip_adjustment_type: cashupRes.data.tip_adjustment_type || 'cash',
            extra_comments: cashupRes.data.extra_comments || ''
          });
          setCalculations({
            cash_over_under: cashupRes.data.cash_over_under,
            card_over_under: cashupRes.data.card_over_under,
            recognised_cash_tips: cashupRes.data.recognised_cash_tips,
            recognised_card_tips: cashupRes.data.recognised_card_tips,
            total_recognised_tips: cashupRes.data.total_recognised_tips,
            unrecognised_cash_refunds: cashupRes.data.unrecognised_cash_refunds || 0,
            unrecognised_card_refunds: cashupRes.data.unrecognised_card_refunds || 0
          });
        }
      } catch (e) {
        // No cashup exists yet
      }
    } catch (error) {
      toast.error('Failed to load shift data');
      navigateBack();
    } finally {
      setLoading(false);
    }
  };

  // Calculate all values when form changes
  const calculateValues = useCallback(() => {
    if (!shift) return;
    
    const bankedCash = parseFloat(formData.banked_cash) || 0;
    const confirmedPayouts = parseFloat(formData.confirmed_cash_payouts) || 0;
    const reportedCashTips = parseFloat(formData.reported_cash_tips) || 0;
    const batchReport = parseFloat(formData.batch_report_total) || 0;
    const reportedCardTips = parseFloat(formData.reported_card_tips) || 0;
    const tipAdjustment = parseFloat(formData.tip_adjustment_amount) || 0;
    const recognisedCashRefunds = parseFloat(formData.recognised_cash_refunds) || 0;
    const recognisedCardRefunds = parseFloat(formData.recognised_card_refunds) || 0;
    
    // Get Loyverse refunds
    const loyverseCashRefunds = shift.cash_refunds || 0;
    const loyverseCardRefunds = shift.card_refunds || 0;
    
    // Calculate unrecognised refunds (these will reduce tips)
    const unrecognisedCashRefunds = Math.max(0, loyverseCashRefunds - recognisedCashRefunds);
    const unrecognisedCardRefunds = Math.max(0, loyverseCardRefunds - recognisedCardRefunds);
    
    // Net cash = Cash Payments - Cash Refunds
    const netCashPayments = (shift.cash_payments || 0) - loyverseCashRefunds;
    
    // Net card = Card Payments - Card Refunds
    const netCardPayments = (shift.card_payments || 0) - loyverseCardRefunds;
    
    // Cash Over/Under = Banked Cash - (Net Cash Payments - Confirmed Payouts + Reported Cash Tips)
    const cashOverUnder = bankedCash - (netCashPayments - confirmedPayouts + reportedCashTips);
    
    // Card Over/Under = Batch Report Total - Net Card Payments
    const cardOverUnder = batchReport - netCardPayments;
    
    // Recognised Cash Tips = Reported Cash Tips - Cash Under - Unrecognised Cash Refunds
    const cashUnder = cashOverUnder < 0 ? Math.abs(cashOverUnder) : 0;
    let recognisedCashTips = reportedCashTips - cashUnder - unrecognisedCashRefunds;
    
    // Recognised Card Tips = Reported Card Tips - Card Under - Unrecognised Card Refunds
    const cardUnder = cardOverUnder < 0 ? Math.abs(cardOverUnder) : 0;
    let recognisedCardTips = reportedCardTips - cardUnder - unrecognisedCardRefunds;
    
    // Apply tip adjustment (can result in negative)
    if (formData.tip_adjustment_type === 'cash') {
      recognisedCashTips = recognisedCashTips + tipAdjustment;
    } else {
      recognisedCardTips = recognisedCardTips + tipAdjustment;
    }
    
    const totalRecognisedTips = recognisedCashTips + recognisedCardTips;
    
    setCalculations({
      cash_over_under: cashOverUnder,
      card_over_under: cardOverUnder,
      recognised_cash_tips: recognisedCashTips,
      recognised_card_tips: recognisedCardTips,
      total_recognised_tips: totalRecognisedTips,
      unrecognised_cash_refunds: unrecognisedCashRefunds,
      unrecognised_card_refunds: unrecognisedCardRefunds
    });
  }, [formData, shift]);

  useEffect(() => {
    calculateValues();
  }, [calculateValues]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleInputBlur = (field, value) => {
    // Evaluate expression on blur
    const evaluated = evaluateExpression(value);
    if (evaluated !== value) {
      setFormData(prev => ({ ...prev, [field]: evaluated.toString() }));
    }
  };

  const handleSave = async () => {
    // Validate required fields
    const requiredFields = [
      { field: 'staff_member', label: 'Staff Member' },
      { field: 'banked_cash', label: 'Banked Cash' },
      { field: 'confirmed_cash_payouts', label: 'Confirmed Cash Payouts' },
      { field: 'reported_cash_tips', label: 'Reported Cash Tips' },
      { field: 'batch_report_total', label: 'Batch Report Total' },
      { field: 'reported_card_tips', label: 'Reported Card Tips' }
    ];
    
    const emptyFields = requiredFields.filter(({ field }) => {
      const value = formData[field];
      return value === '' || value === null || value === undefined;
    });
    
    if (emptyFields.length > 0) {
      toast.error(`Please fill in: ${emptyFields.map(f => f.label).join(', ')}`);
      return;
    }

    if (!formData.staff_member.trim()) {
      toast.error('Please enter the staff member name');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        shift_id: shiftId,
        staff_member: formData.staff_member,
        banked_cash: parseFloat(formData.banked_cash) || 0,
        confirmed_cash_payouts: parseFloat(formData.confirmed_cash_payouts) || 0,
        reported_cash_tips: parseFloat(formData.reported_cash_tips) || 0,
        batch_report_total: parseFloat(formData.batch_report_total) || 0,
        reported_card_tips: parseFloat(formData.reported_card_tips) || 0,
        recognised_cash_refunds: parseFloat(formData.recognised_cash_refunds) || 0,
        recognised_card_refunds: parseFloat(formData.recognised_card_refunds) || 0,
        tip_adjustment_amount: parseFloat(formData.tip_adjustment_amount) || 0,
        tip_adjustment_type: formData.tip_adjustment_type,
        extra_comments: formData.extra_comments || null
      };

      if (cashup) {
        await api.put(`/cashups/${cashup.id}`, payload);
        toast.success('Cashup updated');
      } else {
        const res = await api.post('/cashups', payload);
        setCashup(res.data);
        toast.success('Cashup saved');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    // Validate required fields first
    const requiredFields = [
      { field: 'staff_member', label: 'Staff Member' },
      { field: 'banked_cash', label: 'Banked Cash' },
      { field: 'confirmed_cash_payouts', label: 'Confirmed Cash Payouts' },
      { field: 'reported_cash_tips', label: 'Reported Cash Tips' },
      { field: 'batch_report_total', label: 'Batch Report Total' },
      { field: 'reported_card_tips', label: 'Reported Card Tips' }
    ];
    
    const emptyFields = requiredFields.filter(({ field }) => {
      const value = formData[field];
      return value === '' || value === null || value === undefined;
    });
    
    if (emptyFields.length > 0) {
      toast.error(`Please fill in: ${emptyFields.map(f => f.label).join(', ')}`);
      return;
    }

    if (!formData.staff_member.trim()) {
      toast.error('Please enter the staff member name');
      return;
    }
    
    setConfirming(true);
    try {
      const payload = {
        shift_id: shiftId,
        staff_member: formData.staff_member,
        banked_cash: parseFloat(formData.banked_cash) || 0,
        confirmed_cash_payouts: parseFloat(formData.confirmed_cash_payouts) || 0,
        reported_cash_tips: parseFloat(formData.reported_cash_tips) || 0,
        batch_report_total: parseFloat(formData.batch_report_total) || 0,
        reported_card_tips: parseFloat(formData.reported_card_tips) || 0,
        recognised_cash_refunds: parseFloat(formData.recognised_cash_refunds) || 0,
        recognised_card_refunds: parseFloat(formData.recognised_card_refunds) || 0,
        tip_adjustment_amount: parseFloat(formData.tip_adjustment_amount) || 0,
        tip_adjustment_type: formData.tip_adjustment_type,
        extra_comments: formData.extra_comments || null
      };

      // If no cashup exists, create one first
      let cashupId = cashup?.id;
      if (!cashupId) {
        const res = await api.post('/cashups', payload);
        cashupId = res.data.id;
      } else {
        // Update existing cashup before confirming
        await api.put(`/cashups/${cashupId}`, payload);
      }
      
      // Now confirm
      await api.post(`/cashups/${cashupId}/confirm`);
      toast.success('Cashup confirmed and locked');
      navigateBack();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleRetryAccounting = async () => {
    if (!cashup?.id) return;
    
    setRetryingAccounting(true);
    try {
      const res = await api.post(`/admin/accounting/retry/${cashup.id}`);
      toast.success(`Invoice created: ${res.data.invoice_number}`);
      // Refresh cashup to get updated accounting status
      const cashupRes = await api.get(`/cashups/shift/${shiftId}`);
      if (cashupRes.data) {
        setCashup(cashupRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to accounting');
    } finally {
      setRetryingAccounting(false);
    }
  };

  // Payout matching handlers
  const handlePayoutSelection = (expenseId, checked) => {
    if (checked) {
      setSelectedPayouts([...selectedPayouts, expenseId]);
    } else {
      setSelectedPayouts(selectedPayouts.filter(id => id !== expenseId));
    }
  };

  const handleMatchPayouts = async () => {
    if (selectedPayouts.length === 0) {
      toast.error('Please select at least one receipt to match');
      return;
    }
    
    setMatchingPayouts(true);
    try {
      const res = await api.post('/cash-expenses/match-to-shift', {
        shift_id: shiftId,
        expense_ids: selectedPayouts
      });
      toast.success(res.data.message);
      setSelectedPayouts([]);
      fetchReceipts(shift.business_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to match payouts');
    } finally {
      setMatchingPayouts(false);
    }
  };

  const selectedPayoutTotal = unmatchedPayouts
    .filter(p => selectedPayouts.includes(p.id))
    .reduce((sum, p) => sum + p.amount, 0);

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    // Show 2 decimal places
    return formatMoney(num);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  const isLocked = cashup?.is_confirmed || shift?.is_reconciled;
  const isAdmin = user?.role === 'admin';
  const canEdit = !isLocked || isAdmin;
  const showPayoutReceipts = (shift?.paid_out || 0) > 0 || (parseFloat(formData.confirmed_cash_payouts) || 0) > 0;

  return (
    <div className="page-container animate-slide-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={navigateBack} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Shift Cashup</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {shift.business_name} • {shift.store_name} • {shift.pos_name} • Closed: {formatDateTime(shift.closed_at)}
          </p>
        </div>
        {isLocked && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm">
            <Lock className="h-4 w-4" />
            Confirmed
          </div>
        )}

        {/* Accounting Status - Show if confirmed */}
        {isLocked && (
          <Card className="mt-4" data-testid="accounting-status-card">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Accounting</Label>
                </div>
                <div className="flex items-center gap-2">
                  {cashup?.accounting_status === 'success' ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Pushed - {cashup?.accounting_invoice_number || 'Invoice created'}
                    </Badge>
                  ) : cashup?.accounting_status === 'failed' ? (
                    <>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Failed
                      </Badge>
                      {isAdmin && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleRetryAccounting}
                          disabled={retryingAccounting}
                        >
                          <RefreshCw className={`h-3 w-3 mr-1 ${retryingAccounting ? 'animate-spin' : ''}`} />
                          Retry
                        </Button>
                      )}
                    </>
                  ) : (
                    <Badge variant="outline" className="bg-gray-100 text-gray-600">
                      Not configured
                    </Badge>
                  )}
                </div>
              </div>
              {cashup?.accounting_error && cashup?.accounting_status === 'failed' && (
                <p className="text-xs text-red-600 mt-2">{cashup.accounting_error}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Cashup Grid - Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        
        {/* CASH SECTION */}
        <Card data-testid="cash-section">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-[#006241]">Cash Reconciliation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Staff Member Row */}
            <div className="p-3 border-2 border-[#006241]/30 rounded-lg bg-[#006241]/5">
              <Label className="text-xs text-muted-foreground">
                Staff Member <span className="text-red-500">*</span>
              </Label>
              <Input
                id="staff_member"
                type="text"
                value={formData.staff_member}
                onChange={(e) => handleInputChange('staff_member', e.target.value)}
                placeholder="Enter staff name"
                disabled={!canEdit}
                className="font-medium h-10 mt-1"
                data-testid="input-staff"
                required
              />
            </div>

            {/* Cash Payments Row */}
            <div className="grid grid-cols-2 gap-4 items-center p-3 bg-[#F2F2F0] rounded-lg">
              <div>
                <Label className="text-xs text-muted-foreground">Cash Payments (Loyverse)</Label>
                <p className="text-xl font-mono font-bold">{formatCurrency((shift.cash_payments || 0) - (shift.cash_refunds || 0))}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Banked Cash <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  value={formData.banked_cash}
                  onChange={(e) => handleInputChange('banked_cash', e.target.value)}
                  onBlur={(e) => handleInputBlur('banked_cash', e.target.value)}
                  placeholder="Enter amount"
                  disabled={!canEdit}
                  className="font-mono text-lg h-12"
                  data-testid="input-banked-cash"
                  required
                />
              </div>
            </div>

            {/* Cash Payouts Row */}
            <div className="grid grid-cols-2 gap-4 items-center p-3 bg-[#F2F2F0] rounded-lg">
              <div>
                <Label className="text-xs text-muted-foreground">Paid Out (Loyverse)</Label>
                <p className="text-xl font-mono font-bold">{formatCurrency(shift.paid_out)}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Confirmed Cash Payouts <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  value={formData.confirmed_cash_payouts}
                  onChange={(e) => handleInputChange('confirmed_cash_payouts', e.target.value)}
                  onBlur={(e) => handleInputBlur('confirmed_cash_payouts', e.target.value)}
                  placeholder="Enter amount"
                  disabled={!canEdit}
                  className="font-mono text-lg h-12"
                  data-testid="input-payouts"
                  required
                />
              </div>
            </div>

            <ShiftReceipts
              receipts={linkedReceipts}
              confirmedPayouts={formData.confirmed_cash_payouts}
              canEdit={canEdit}
              onChanged={() => fetchReceipts(shift.business_id)}
            />

            {/* Pending receipt linking - shown once a payout amount is entered */}
            {showPayoutReceipts && unmatchedPayouts.length === 0 && (
              <p className="text-xs text-muted-foreground px-1" data-testid="no-pending-receipts">No pending DocHub receipts for this business.</p>
            )}
            {(showPayoutReceipts && unmatchedPayouts.length > 0) && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-blue-600" />
                  <Label className="text-sm font-medium text-blue-800">Pending Receipts to Link (DocHub)</Label>
                </div>
                <p className="text-xs text-blue-600">
                  Pending cash movements (from DocHub) waiting to be assigned to a shift. Tick the ones paid out in this shift; they are confirmed when the shift is confirmed.
                </p>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {unmatchedPayouts.map((payout) => (
                    <div 
                      key={payout.id}
                      className={`flex items-center gap-3 p-2 rounded border ${
                        selectedPayouts.includes(payout.id) 
                          ? 'bg-blue-100 border-blue-300' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <Checkbox
                        id={`payout-${payout.id}`}
                        checked={selectedPayouts.includes(payout.id)}
                        onCheckedChange={(checked) => handlePayoutSelection(payout.id, checked)}
                        disabled={!canEdit}
                        data-testid={`pending-receipt-${payout.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-medium">{formatCurrency(payout.amount)}</span>
                          <span className="text-sm text-gray-600 truncate">{payout.supplier_reason}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{payout.expense_date}</span>
                          {payout.branch_tag && (
                            <Badge variant="outline" className="text-xs">{payout.branch_tag}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                  <div className="text-sm">
                    <span className="text-blue-700">Selected: </span>
                    <span className="font-mono font-medium">{formatCurrency(selectedPayoutTotal)}</span>
                    <span className="text-blue-600"> / {formatCurrency(parseFloat(formData.confirmed_cash_payouts) || 0)}</span>
                    {selectedPayouts.length > 0 && Math.round((selectedPayoutTotal + linkedReceipts.reduce((t, r) => t + r.amount, 0) - (parseFloat(formData.confirmed_cash_payouts) || 0)) * 100) !== 0 && (
                      <span className="block text-xs text-amber-700" data-testid="link-receipts-warning">Linked + selected receipts don't equal the confirmed payouts</span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={handleMatchPayouts}
                    disabled={selectedPayouts.length === 0 || matchingPayouts || !canEdit}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="link-receipts-btn"
                  >
                    {matchingPayouts ? 'Linking...' : `Link ${selectedPayouts.length} Receipt(s)`}
                  </Button>
                </div>
              </div>
            )}

            {/* Cash Refunds Row - Only show if there are Loyverse refunds */}
            {(shift.cash_refunds > 0) && (
              <div className="grid grid-cols-2 gap-4 items-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Cash Refunds (Loyverse)</Label>
                  <p className="text-xl font-mono font-bold text-orange-700">{formatCurrency(shift.cash_refunds)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Recognised Cash Refunds
                  </Label>
                  <Input
                    type="text"
                    value={formData.recognised_cash_refunds}
                    onChange={(e) => handleInputChange('recognised_cash_refunds', e.target.value)}
                    onBlur={(e) => handleInputBlur('recognised_cash_refunds', e.target.value)}
                    placeholder="0"
                    disabled={!canEdit}
                    className="font-mono text-lg h-12"
                    data-testid="input-recognised-cash-refunds"
                  />
                  {calculations.unrecognised_cash_refunds > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      Unrecognised: {formatCurrency(calculations.unrecognised_cash_refunds)} (reduces tips)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Reported Cash Tips */}
            <div className="p-3 border rounded-lg">
              <Label className="text-xs text-muted-foreground">
                Reported Cash Tips <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                value={formData.reported_cash_tips}
                onChange={(e) => handleInputChange('reported_cash_tips', e.target.value)}
                onBlur={(e) => handleInputBlur('reported_cash_tips', e.target.value)}
                placeholder="Enter amount"
                disabled={!canEdit}
                className="font-mono text-lg h-12 mt-1"
                data-testid="input-cash-tips"
                required
              />
            </div>

            <Separator />

            {/* Cash Over/Under */}
            <div className={`p-4 rounded-lg ${
              calculations.cash_over_under > 0 ? 'bg-green-50 border border-green-200' :
              calculations.cash_over_under < 0 ? 'bg-red-50 border border-red-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <Label className="text-xs text-muted-foreground">Cash Over/Under</Label>
              <p className={`text-2xl font-mono font-bold ${
                calculations.cash_over_under > 0 ? 'text-green-700' :
                calculations.cash_over_under < 0 ? 'text-red-700' :
                'text-gray-700'
              }`}>
                {calculations.cash_over_under >= 0 ? '+' : ''}{formatCurrency(calculations.cash_over_under)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                = Banked - Cash Payments - Payouts - Tips
              </p>
            </div>

            {/* Recognised Cash Tips */}
            <div className={`p-3 rounded-lg ${
              calculations.recognised_cash_tips < 0 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-[#006241]/5 border border-[#006241]/20'
            }`}>
              <Label className="text-xs text-muted-foreground">Recognised Cash Tips</Label>
              <p className={`text-xl font-mono font-bold ${
                calculations.recognised_cash_tips < 0 ? 'text-red-700' : 'text-[#006241]'
              }`}>
                {calculations.recognised_cash_tips < 0 ? '' : ''}{formatCurrency(calculations.recognised_cash_tips)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                = Reported Tips - Cash Under{calculations.unrecognised_cash_refunds > 0 ? ' - Unrecognised Refunds' : ''}
              </p>
            </div>

          </CardContent>
        </Card>

        {/* CARD SECTION */}
        <Card data-testid="card-section">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-[#006241]">Card Reconciliation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Card Payments Row */}
            <div className="grid grid-cols-2 gap-4 items-center p-3 bg-[#F2F2F0] rounded-lg">
              <div>
                <Label className="text-xs text-muted-foreground">Card Payments (Loyverse)</Label>
                <p className="text-xl font-mono font-bold">{formatCurrency((shift.card_payments || 0) - (shift.card_refunds || 0))}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Batch Report Total <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  value={formData.batch_report_total}
                  onChange={(e) => handleInputChange('batch_report_total', e.target.value)}
                  onBlur={(e) => handleInputBlur('batch_report_total', e.target.value)}
                  placeholder="Enter amount"
                  disabled={!canEdit}
                  className="font-mono text-lg h-12"
                  data-testid="input-batch-report"
                  required
                />
              </div>
            </div>

            {/* EFT Info */}
            {((shift.eft_payments || 0) - (shift.eft_refunds || 0)) > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-xs text-muted-foreground">EFT Payments (Loyverse)</Label>
                <p className="text-xl font-mono font-bold text-blue-700">{formatCurrency((shift.eft_payments || 0) - (shift.eft_refunds || 0))}</p>
              </div>
            )}

            {/* Card Refunds Row - Only show if there are Loyverse refunds */}
            {(shift.card_refunds > 0) && (
              <div className="grid grid-cols-2 gap-4 items-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Card Refunds (Loyverse)</Label>
                  <p className="text-xl font-mono font-bold text-orange-700">{formatCurrency(shift.card_refunds)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Recognised Card Refunds
                  </Label>
                  <Input
                    type="text"
                    value={formData.recognised_card_refunds}
                    onChange={(e) => handleInputChange('recognised_card_refunds', e.target.value)}
                    onBlur={(e) => handleInputBlur('recognised_card_refunds', e.target.value)}
                    placeholder="0"
                    disabled={!canEdit}
                    className="font-mono text-lg h-12"
                    data-testid="input-recognised-card-refunds"
                  />
                  {calculations.unrecognised_card_refunds > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      Unrecognised: {formatCurrency(calculations.unrecognised_card_refunds)} (reduces tips)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Reported Card Tips */}
            <div className="p-3 border rounded-lg">
              <Label className="text-xs text-muted-foreground">
                Reported Card Tips <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                value={formData.reported_card_tips}
                onChange={(e) => handleInputChange('reported_card_tips', e.target.value)}
                onBlur={(e) => handleInputBlur('reported_card_tips', e.target.value)}
                placeholder="Enter amount"
                disabled={!canEdit}
                className="font-mono text-lg h-12 mt-1"
                data-testid="input-card-tips"
                required
              />
            </div>

            <Separator />

            {/* Card Over/Under */}
            <div className={`p-4 rounded-lg ${
              calculations.card_over_under > 0 ? 'bg-green-50 border border-green-200' :
              calculations.card_over_under < 0 ? 'bg-red-50 border border-red-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <Label className="text-xs text-muted-foreground">Card Over/Under</Label>
              <p className={`text-2xl font-mono font-bold ${
                calculations.card_over_under > 0 ? 'text-green-700' :
                calculations.card_over_under < 0 ? 'text-red-700' :
                'text-gray-700'
              }`}>
                {calculations.card_over_under >= 0 ? '+' : ''}{formatCurrency(calculations.card_over_under)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                = Batch Report - Card Payments
              </p>
            </div>

            {/* Recognised Card Tips */}
            <div className={`p-3 rounded-lg ${
              calculations.recognised_card_tips < 0 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-[#006241]/5 border border-[#006241]/20'
            }`}>
              <Label className="text-xs text-muted-foreground">Recognised Card Tips</Label>
              <p className={`text-xl font-mono font-bold ${
                calculations.recognised_card_tips < 0 ? 'text-red-700' : 'text-[#006241]'
              }`}>
                {calculations.recognised_card_tips < 0 ? '' : ''}{formatCurrency(calculations.recognised_card_tips)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                = Reported Tips - Card Under{calculations.unrecognised_card_refunds > 0 ? ' - Unrecognised Refunds' : ''}
              </p>
            </div>

          </CardContent>
        </Card>
      </div>

      {/* Tip Adjustment & Summary */}
      <div className="grid gap-6 lg:grid-cols-2 mt-6">
        
        {/* Tip Adjustment */}
        <Card data-testid="tip-adjustment-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Manual Tip Adjustment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Adjustment Amount (+/-)</Label>
                <Input
                  type="text"
                  value={formData.tip_adjustment_amount}
                  onChange={(e) => handleInputChange('tip_adjustment_amount', e.target.value)}
                  onBlur={(e) => handleInputBlur('tip_adjustment_amount', e.target.value)}
                  placeholder="0"
                  disabled={!canEdit}
                  className="font-mono text-lg h-12 mt-1"
                  data-testid="input-tip-adjustment"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Apply To</Label>
                <RadioGroup
                  value={formData.tip_adjustment_type}
                  onValueChange={(value) => handleInputChange('tip_adjustment_type', value)}
                  disabled={!canEdit}
                  className="flex gap-4 mt-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cash" id="adj-cash" />
                    <Label htmlFor="adj-cash" className="font-normal">Cash Tips</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="card" id="adj-card" />
                    <Label htmlFor="adj-card" className="font-normal">Card Tips</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Recognised Tips */}
        <Card data-testid="total-tips-card" className={`${
          calculations.total_recognised_tips < 0 
            ? 'bg-red-600' 
            : 'bg-[#006241]'
        } text-white`}>
          <CardContent className="pt-6">
            <Label className="text-white/70 text-sm">Total Recognised Tips</Label>
            <p className="text-4xl font-mono font-bold mt-2">
              {formatCurrency(calculations.total_recognised_tips)}
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-sm text-white/70">
              <div>
                <span>Cash: </span>
                <span className="font-mono">{formatCurrency(calculations.recognised_cash_tips)}</span>
              </div>
              <div>
                <span>Card: </span>
                <span className="font-mono">{formatCurrency(calculations.recognised_card_tips)}</span>
              </div>
              {(parseFloat(formData.tip_adjustment_amount) || 0) !== 0 && (
                <div className="w-full pt-1 border-t border-white/20 mt-1">
                  <span>Includes {formData.tip_adjustment_type === 'cash' ? 'Cash' : 'Card'} Adjustment: </span>
                  <span className="font-mono">
                    {(parseFloat(formData.tip_adjustment_amount) || 0) >= 0 ? '+' : ''}
                    {formatCurrency(parseFloat(formData.tip_adjustment_amount) || 0)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comments */}
      <Card className="mt-6" data-testid="comments-card">
        <CardContent className="pt-6">
          <Label htmlFor="comments">Extra Comments / Notes</Label>
          <Textarea
            id="comments"
            value={formData.extra_comments}
            onChange={(e) => handleInputChange('extra_comments', e.target.value)}
            placeholder="Any additional notes about this cashup..."
            disabled={!canEdit}
            rows={2}
            className="mt-2"
            data-testid="input-comments"
          />
        </CardContent>
      </Card>

      {/* Warning for discrepancies */}
      {(calculations.cash_over_under < -50 || calculations.card_over_under < -50) && (
        <Alert variant="destructive" className="mt-6" data-testid="discrepancy-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Significant Discrepancy</AlertTitle>
          <AlertDescription>
            There is a significant shortage. Please verify the amounts before confirming.
          </AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      {canEdit && (
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 h-12"
            variant="outline"
            data-testid="save-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                className="flex-1 h-12 bg-[#006241] hover:bg-[#004d33]"
                disabled={!formData.staff_member || !formData.banked_cash || !formData.batch_report_total}
                data-testid="confirm-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm & Lock
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Cashup?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will lock the cashup and mark shift <strong>{shift.shift_number}</strong> as reconciled.
                  {!isAdmin && " Only an admin can edit after confirmation."}
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Cash Over/Under: <span className="font-mono">{formatCurrency(calculations.cash_over_under)}</span></div>
                      <div>Card Over/Under: <span className="font-mono">{formatCurrency(calculations.card_over_under)}</span></div>
                      <div className="col-span-2 pt-2 border-t mt-2">
                        <strong>Total Recognised Tips: <span className="font-mono">{formatCurrency(calculations.total_recognised_tips)}</span></strong>
                      </div>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="bg-[#006241] hover:bg-[#004d33]"
                >
                  {confirming ? 'Confirming...' : 'Confirm'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Confirmed info */}
      {cashup?.is_confirmed && (
        <Alert className="mt-6" data-testid="confirmed-info">
          <Info className="h-4 w-4" />
          <AlertTitle>Confirmed</AlertTitle>
          <AlertDescription>
            Confirmed by {cashup.confirmed_by} on {formatDateTime(cashup.confirmed_at)}
          </AlertDescription>
        </Alert>
      )}

      {/* Helper text */}
      <p className="text-xs text-muted-foreground mt-4 text-center">
        Tip: You can enter calculations in any field (e.g., "100+50" will become "150")
      </p>
    </div>
  );
};

export default CashupPage;
